import { noteSocketClose } from "./access";
import { wsUrl } from "./wsUrl";

/** Image attachment on an outgoing chat frame — raw base64, no `data:` prefix
 * (the server wraps it into an ImageContent for the vision provider). */
export interface WsAttachment {
	kind: "image";
	data: string;
	media_type: string;
}

/** Server → client message shape on the chat WebSocket.
 *
 * `in_flight` is the P3.2 auto-resume hello handshake — fired immediately
 * on every WS connect when the server's TurnRegistry shows an in-flight
 * turn for this conversation_id. Client uses it to render the
 * "still working" indicator on reconnect without waiting for status
 * polling to catch up. The `state` payload mirrors `TurnState.to_dict()`
 * on the Python side.
 */
export interface WSMessage {
	type:
		| "chunk"
		| "error"
		| "title"
		| "tool_status"
		| "pong"
		| "in_flight"
		| "correlation_id"
		| "background_result"
		| "elicitation_request"
		| "confirmation_request"
		| "connect_request"
		| "routine_draft"
		| "command_result"
		| "browser_screen"
		| "deck_preview"
		| "agent"
		| "agent_message";
	delta?: string;
	done?: boolean;
	message?: string;
	title?: string;
	tool?: string;
	status?: string;
	state?: TurnStateSnapshot;
	/** MCP elicitation (elicitation_request frames): pending-request id + server. */
	request_id?: string;
	server?: string;
	/** Confirmation gate (confirmation_request frames): a tool call is
	 * awaiting the user's approval — payload for the inline bar. `tool`
	 * is shared with tool_status frames above. */
	contract_action?: string;
	summary?: string;
	reason?: string;
	/** A2: the card may offer "Always allow" for this ask (YAML-decided server-side). */
	always_allowed?: boolean;
	/** Bot threads (A1): the bot answering THIS turn, sent before the first delta. */
	agent_key?: string;
	agent_name?: string;
	/** B5 group threads (agent_message frames): a participant's reply, persisted
	 * server-side and pushed so it appears under its own name without a reload. */
	message_id?: string;
	content?: string;
	from_agent?: string;
	/** Connect card (connect_request frames, bots A2): a tool refused because
	 * its integration is not connected. `tool` is shared with the frames above. */
	integration?: string;
	settings_anchor?: string;
	cause?: string;
	remedies?: { kind: "auto" | "manual" | "code"; label: string; detail?: string; action?: string }[];
	/** Routine draft (routine_draft frames, bots A3): the agent's proposal the user decides on. */
	draft?: { id: string; agent_key: string; agent_name?: string; name: string; task: string; schedule: string; deliver_to: string; steps?: string[] };
	/** Command output (command_result frames, bots B1): a run_command result for the dock. */
	result?: { id?: string; title?: string; command: string; cwd: string; exit_code: number | null; stdout: string; stderr: string; truncated?: boolean; timed_out?: boolean; duration_ms?: number };
	/** The agent's browser screen a read just used (browser_screen frames, bots B2): the dock opens it. */
	screen?: { id: string; url?: string; title?: string; agent_key?: string };
	/** A deck create_presentation just wrote (deck_preview frames, 2026-09-10):
	 *  the whole self-contained HTML document, rendered on the side canvas. */
	deck?: { id: string; title: string; slides?: number; path?: string; html?: string;
		/** A created DOCUMENT rides the same frame with markdown `content` (2026-09-19). */
		content?: string; language?: string };
	/**
	 * P4.5 — speculative streaming retract. When set on a "chunk" message,
	 * the client MUST remove the trailing N characters from the streaming
	 * message buffer BEFORE appending ``delta`` (which may be empty for
	 * pure-retract events, or carry replacement text). Sent only when the
	 * server's speculative-streaming engine detects that already-streamed
	 * draft text diverged from the real model's output. Clients that
	 * ignore this field MAY end up with stale draft chars on screen until
	 * the next real chunk overwrites them — the final text is still
	 * correct on `done`, only mid-stream preview is wrong.
	 */
	replace_after?: number;
	/**
	 * P4.7 (2026-05-19) — chat turn's correlation id. Server sends this
	 * as the FIRST message of every turn (type === "correlation_id") so
	 * the UI can stamp it on the latest assistant message and match
	 * activity events emitted by tools running inside the same scope.
	 * Surfaced inline in the chat bubble as a progress indicator
	 * (e.g. "Researching… 47s · Searching the web") via
	 * useBackgroundActivityToasts and the chat-store's setMessageProgress
	 * action. Older clients that don't read it see byte-identical
	 * pre-P4.7 behavior — the empty-delta chunk is a pure signal.
	 */
	correlation_id?: string;
	/**
	 * 2026-05-23 — LLM model id that produced this assistant turn
	 * (e.g. ``"anthropic/claude-sonnet-4-5"``, ``"ollama/qwen2.5:14b"``).
	 * Set on the final ``done=true`` chunk. UI stamps it on the streaming
	 * message via finalizeLastMessage so MessageBubble can render a per-
	 * message "via <model>" chip. Hybrid routing means different turns
	 * can land on different models; per-message granularity reflects
	 * that. Empty / absent on older servers — UI just skips the chip.
	 */
	model?: string;
	/**
	 * 2026-07-06 — set on the terminal (`done: true`) chunk when the user
	 * interrupted the turn via the Stop button. Lets the UI treat the reply
	 * as stopped-by-user rather than a clean completion. Absent on older
	 * servers and on normal completions.
	 */
	stopped?: boolean;
}

/** Mirrors `agent.turn_registry.TurnState.to_dict()`. */
export interface TurnStateSnapshot {
	conversation_id: string;
	started_at: string;
	phase:
		| "thinking"
		| "tool"
		| "streaming"
		| "done"
		| "error"
		| "cancelled";
	message_id: string | null;
	last_tool_name: string | null;
	tools_attempted: string[];
	tools_succeeded: number;
	tools_failed: number;
	last_chunk_at: string | null;
	round_index: number;
	error: string | null;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000]; // exponential backoff
const PING_INTERVAL = 25_000; // 25s — keep connection alive
/** A handshake still CONNECTING after this is closed so the retry path runs.
 *  Measured 2026-09-10: a page at http://localhost:8000 whose first attempt hung
 *  on IPv6 loopback fired neither open nor close — no close, no reconnect, "not
 *  connected" for good. */
export const CONNECT_TIMEOUT_MS = 10_000;

/** Dispatched on `window` whenever the chat socket opens or closes
 *  (`detail: { connected, code? }`) — the node-reachability hook probes on a
 *  drop, which is the first thing a person notices when the node is gone (N3). */
export const WS_STATE_EVENT = "agntspace:ws-state";

function announceState(connected: boolean, code?: number, retrying?: boolean): void {
	if (typeof window === "undefined") return;
	// `retrying`: a reconnect IS scheduled — the composer's text claims one only then (2026-09-10).
	window.dispatchEvent(new CustomEvent(WS_STATE_EVENT, { detail: { connected, code, retrying: !!retrying } }));
}

export class AgntWebSocket {
	private ws: WebSocket | null = null;
	private url: string;
	private onMessage: ((msg: WSMessage) => void) | null = null;
	private onError: ((err: Event) => void) | null = null;
	private onClose: (() => void) | null = null;
	/** Connection state for the UI (2026-09-07): true on open, false on every close — a
	 * send refused for a dead socket must be able to SAY so instead of doing nothing. */
	onStateChange: ((connected: boolean) => void) | null = null;
	private reconnectAttempt = 0;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private pingTimer: ReturnType<typeof setInterval> | null = null;
	/**
	 * Wall-clock ms of the last proof this socket is alive: open, any frame,
	 * the pong included. The turn watchdog in useChat reads it — a turn can
	 * be silent for minutes (a reasoning model's first token, a provider
	 * retry) while the socket keeps answering pings, and silence must never
	 * read as death (2026-09-10).
	 */
	public lastAliveAt = 0;
	private intentionallyClosed = false;

	constructor(conversationId: string) {
		this.url = wsUrl(`/ws/chat/${conversationId}`);
	}

	connect(
		onMessage: (msg: WSMessage) => void,
		onError: (err: Event) => void,
		onClose: () => void,
	): void {
		this.onMessage = onMessage;
		this.onError = onError;
		this.onClose = onClose;
		this.intentionallyClosed = false;
		this._connect();
	}

	private _connect(): void {
		const ws = new WebSocket(this.url);
		this.ws = ws;
		const connectTimer = setTimeout(() => {
			if (ws.readyState === WebSocket.CONNECTING) {
				try {
					ws.close();
				} catch {
					/* already gone */
				}
			}
		}, CONNECT_TIMEOUT_MS);

		this.ws.onopen = () => {
			clearTimeout(connectTimer);
			this.reconnectAttempt = 0;
			this.lastAliveAt = Date.now();
			this._startPing();
			this.onStateChange?.(true);
			announceState(true);
		};

		this.ws.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data) as WSMessage;
				// Every frame is proof of life — the pong included, which is the
				// whole point of the ping: the turn watchdog reads lastAliveAt,
				// never the chunk clock.
				this.lastAliveAt = Date.now();
				// Ignore pong responses
				if (data.type === "pong") return;
				this.onMessage?.(data);
			} catch {
				this.onError?.(new Event("parse_error"));
			}
		};

		this.ws.onerror = (err) => {
			this.onError?.(err);
		};

		this.ws.onclose = (ev) => {
			clearTimeout(connectTimer);
			noteSocketClose(ev?.code);
			this.onStateChange?.(false);
			announceState(false, ev?.code, !this.intentionallyClosed);
			this._stopPing();
			if (!this.intentionallyClosed) {
				this._scheduleReconnect();
			}
			this.onClose?.();
		};
	}

	private _startPing(): void {
		this._stopPing();
		this.pingTimer = setInterval(() => {
			if (this.ws?.readyState === WebSocket.OPEN) {
				this.ws.send(JSON.stringify({ type: "ping" }));
			}
		}, PING_INTERVAL);
	}

	private _stopPing(): void {
		if (this.pingTimer) {
			clearInterval(this.pingTimer);
			this.pingTimer = null;
		}
	}

	private _scheduleReconnect(): void {
		if (this.intentionallyClosed) return;
		const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
		this.reconnectTimer = setTimeout(() => {
			this.reconnectAttempt++;
			this._connect();
		}, delay);
	}

	// Returns true if the frame was actually placed on the wire. False when
	// the socket isn't OPEN (e.g. it raced closed between the caller's
	// `connected` check and here) — the caller uses this to avoid adding a
	// phantom "Thinking…" bubble for a message that never left the browser.
	send(message: string, model?: string, attachments?: WsAttachment[]): boolean {
		if (this.ws?.readyState === WebSocket.OPEN) {
			// free-model (slice 6a): a concrete model pins THIS turn (highest
			// precedence). Omitted/empty → backend falls through to the saved
			// per-conversation pin → global default → chat slot.
			const payload: {
				message: string;
				effective_model?: string;
				attachments?: WsAttachment[];
			} = { message };
			if (model && model.trim()) payload.effective_model = model.trim();
			if (attachments && attachments.length) payload.attachments = attachments;
			this.ws.send(JSON.stringify(payload));
			return true;
		}
		return false;
	}

	// Ask the server to regenerate the assistant reply against the current
	// conversation history WITHOUT inserting a new user row. Used after a
	// user-message edit (REST already truncated the trailing reply) and on
	// the assistant-toolbar Retry button. The text is the prompt to reply
	// to — usually the last user message; passing it explicitly saves the
	// server one DB lookup.
	// ``branchGroupId`` (message branching, 2026-07-07): when the REST
	// regenerate/edit route minted a sibling group, echo it here so the fresh
	// reply is stamped into the same group and becomes a navigable branch.
	sendRegenerate(message: string, branchGroupId?: string | null): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			const payload: Record<string, unknown> = { regenerate: true, message };
			if (branchGroupId) payload.branch_group_id = branchGroupId;
			this.ws.send(JSON.stringify(payload));
		}
	}

	// Interrupt the in-flight turn. The server races this frame against the
	// stream task and cancels generation, then sends a terminal `done`
	// (stopped=true) frame so the partial reply is finalized. No-op if the
	// socket isn't open (nothing to stop).
	sendStop(): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify({ type: "stop" }));
		}
	}

	close(): void {
		this.intentionallyClosed = true;
		this._stopPing();
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		this.ws?.close();
		this.ws = null;
	}

	get connected(): boolean {
		return this.ws?.readyState === WebSocket.OPEN;
	}
}
